import React, { useState, useEffect, useCallback } from 'react';
import { User, userApi } from '../../services/api';
import UserForm from './UserForm';
import './UserList.scss';

interface UserListProps {}

const UserList: React.FC<UserListProps> = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  const fetchUsers = useCallback(async (resetPage = false) => {
    try {
      if (resetPage) {
        setIsSearching(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const params: any = {
        page: resetPage ? 1 : currentPage,
        limit: 20,
      };
      
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (filterType) params.user_type = filterType;
      
      const response = await userApi.getUsers(params);
      
      if (response.success) {
        // Ensure users is always an array
        const data = response.data as any;
        const userData = Array.isArray(data) ? data : 
                        (data?.users || data?.data || []);
        setUsers(userData);
        if (response.pagination) {
          setTotalPages(response.pagination.pages);
          setTotalUsers(response.pagination.total);
          if (resetPage) {
            setCurrentPage(1);
          }
        }
      } else {
        setUsers([]);
        setError('Nepavyko užkrauti naudotojų');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Klaida užkraunant naudotojus');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [currentPage, searchTerm, filterType]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchUsers(true); // Reset page when searching
    }, 300); // Reduced delay for better UX

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterType]);

  // Separate effect for pagination (no debounce needed)
  useEffect(() => {
    if (currentPage > 1) {
      fetchUsers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);


  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDelete = async (user: User) => {
    if (window.confirm(`Ar tikrai norite ištrinti naudotoją ${user.email}?`)) {
      try {
        const response = await userApi.deleteUser(user.id!);
        if (response.success) {
          fetchUsers(); // Refresh list
          alert('Naudotojas sėkmingai ištrintas');
        } else {
          alert('Nepavyko ištrinti naudotojo');
        }
      } catch (err: any) {
        alert(err.response?.data?.message || 'Klaida trinant naudotoją');
      }
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingUser(null);
    fetchUsers(); // Refresh list
  };

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case 'employee':
        return 'Darbuotojas';
      case 'employer':
        return 'Darbdavys';
      case 'admin':
        return 'Administratorius';
      default:
        return type;
    }
  };

  const getStatusBadge = (isActive: boolean, emailVerified: boolean) => {
    if (!isActive) return <span className="status-badge inactive">Neaktyvus</span>;
    if (!emailVerified) return <span className="status-badge unverified">Nepatvirtintas</span>;
    return <span className="status-badge active">Aktyvus</span>;
  };

  if (loading) {
    return (
      <div className="user-list">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  return (
    <div className="user-list">
      <div className="user-list__header">
        <h1>Naudotojų Valdymas</h1>
        <div className="user-list__stats">
          <span>Iš viso naudotojų: {totalUsers}</span>
        </div>
      </div>

      <div className="user-list__controls">
        <div className="search-form">
          <input
            type="text"
            placeholder="Ieškoti pagal el. paštą, vardą ar kompaniją..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="search-indicator">
            {isSearching && (
              <span className="search-loading">🔍 Ieškoma...</span>
            )}
            {searchTerm && !isSearching && !loading && (
              <span className="search-results">Rasta {totalUsers} rezultatų</span>
            )}
          </div>
        </div>

        <div className="filter-controls">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="">Visi tipai</option>
            <option value="employee">Darbuotojai</option>
            <option value="employer">Darbdaviai</option>
            <option value="admin">Administratoriai</option>
          </select>

          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
          >
            + Pridėti Naudotoją
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="user-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>El. paštas</th>
              <th>Vardas Pavardė</th>
              <th>Tipas</th>
              <th>Statusas</th>
              <th>Kompanija</th>
              <th>Sukurta</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>{user.email}</td>
                <td>
                  {user.profile?.first_name || user.profile?.last_name
                    ? `${user.profile.first_name || ''} ${user.profile.last_name || ''}`.trim()
                    : '-'}
                </td>
                <td>
                  <span className={`user-type ${user.user_type}`}>
                    {getUserTypeLabel(user.user_type)}
                  </span>
                </td>
                <td>{getStatusBadge(user.status === 'active', user.email_verified)}</td>
                <td>{user.profile?.company_name || '-'}</td>
                <td>
                  {user.created_at
                    ? new Date(user.created_at).toLocaleDateString('lt-LT')
                    : '-'}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => handleEdit(user)}
                      className="btn btn-sm btn-secondary"
                    >
                      Redaguoti
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="btn btn-sm btn-danger"
                      disabled={user.user_type === 'admin'}
                    >
                      Ištrinti
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && !loading && (
          <div className="no-data">
            {searchTerm || filterType ? 'Naudotojų nerasta' : 'Nėra naudotojų'}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="btn btn-sm"
          >
            Ankstesnis
          </button>
          
          <span className="pagination-info">
            {currentPage} iš {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="btn btn-sm"
          >
            Kitas
          </button>
        </div>
      )}

      {showForm && (
        <UserForm
          user={editingUser}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default UserList;
